import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  differentPinValidator,
  sameFieldsValidator,
} from '@app/components/commons/forms/formly-validators';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { GlobalState } from '@app/store/store';
import { Button } from '@infocert/ng-ui-kit/models/button';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-pin',
  templateUrl: './pin.component.html',
  styleUrls: ['./pin.component.scss'],
  standalone: false,
})
export class PinPageComponent implements OnInit {
  constructor(
    private certificateService: CertificateService,
    private otpService: OtpService,
    private translateService: TranslateService,
    private store: Store<GlobalState>
  ) {}

  loading = false;
  showStep1: any = false;
  showStep2: any = false;
  step1Attempted = false;
  step2Attempted = false;
  private validationAttemptSubscriptions: Subscription[] = [];
  showWarning = true;
  showSuccess = false;

  showPinInfo: boolean = false;

  get cardButton(): Button {
    const label = this.translateService.instant('PIN_PAGE.CARD.ACTION');
    return {
      label: label,
      altText: label,
      color: 'text',
      onClick: () => {
        this.openModal('PIN');
      },
    };
  }

  ngOnInit(): void {
    this.store
      .pipe(
        untilDestroyed(this),
        tap(store => {
          if (store.session) {
            this.showPinInfo =
              store.session.automatic &&
              !store.session.icssUser &&
              !store.session?.trissUser;
          }
        })
      )
      .subscribe();

    this.setupValidationAttemptReset();
  }

  private setupValidationAttemptReset(): void {
    this.validationAttemptSubscriptions.forEach(sub => sub.unsubscribe());
    this.validationAttemptSubscriptions = [];

    this.validationAttemptSubscriptions.push(
      this.oldPin.valueChanges.subscribe(() => {
        if (this.step1Attempted) this.step1Attempted = false;
      })
    );
    this.validationAttemptSubscriptions.push(
      this.newPin.valueChanges.subscribe(() => {
        if (this.step1Attempted) this.step1Attempted = false;
      })
    );
    this.validationAttemptSubscriptions.push(
      this.confirmPin.valueChanges.subscribe(() => {
        if (this.step1Attempted) this.step1Attempted = false;
      })
    );
    this.validationAttemptSubscriptions.push(
      this.otp.valueChanges.subscribe(() => {
        if (this.step2Attempted) this.step2Attempted = false;
      })
    );
  }

  oldPin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  newPin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  confirmPin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  otp = new UntypedFormControl('', [
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  form = new UntypedFormGroup(
    {
      oldPin: this.oldPin,
      newPin: this.newPin,
      confirmPin: this.confirmPin,
      otp: this.otp,
    },
    {
      validators: [
        differentPinValidator('newPin', 'oldPin'),
        sameFieldsValidator('newPin', 'confirmPin'),
      ],
    }
  );

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  get formErrors() {
    const errors = this.form.errors;
    return errors;
  }

  // Mostra il mismatch solo quando entrambi i PIN hanno raggiunto la lunghezza minima.
  hasPinsAtMinLength(minLength: number = 8): boolean {
    const newPinValue = (this.newPin?.value ?? '').toString();
    const confirmPinValue = (this.confirmPin?.value ?? '').toString();
    return newPinValue.length >= minLength && confirmPinValue.length >= minLength;
  }

  onSubmit() {
    this.loading = true;
    // stop here if form is invalid
    if (this.form.invalid) {
      this.step2Attempted = true;
      this.form.markAllAsTouched();
      this.loading = false;
      return;
    }

    this.certificateService
      //@ts-ignore
      .updatePIN(
        this.f['oldPin'].value.trim(),
        this.f['newPin'].value.trim(),
        this.f['otp'].value.trim()
      )
      .subscribe({
        next: async response => {
          console.log('🚀 ~ PinPageComponent ~ onSubmit ~ response', response);

          this.loading = false;

          // Se tutto va bene

          this.showStep1 = false;
          this.showStep2 = false;
          this.closeModal();
          this.showSuccess = true;
        },
        error: error => {
          console.error(error);
          this.goToStepOne();
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  clearForm() {
    this.step1Attempted = false;
    this.step2Attempted = false;
    this.oldPin = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(8),
    ]);

    this.newPin = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(8),
    ]);

    this.confirmPin = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(8),
    ]);

    this.otp = new UntypedFormControl('', [
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);

    this.form = new UntypedFormGroup(
      {
        oldPin: this.oldPin,
        newPin: this.newPin,
        confirmPin: this.confirmPin,
        otp: this.otp,
      },
      {
        validators: [
          differentPinValidator('newPin', 'oldPin'),
          sameFieldsValidator('newPin', 'confirmPin'),
        ],
      }
    );
    this.setupValidationAttemptReset();
  }

  goToSecondStep() {
    if (this.form.invalid) {
      this.step1Attempted = true;
      this.form.markAllAsTouched();
      return;
    }
    this.step1Attempted = false;
    this.sendOtp();
    this.f['otp'].addValidators([
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);
    this.f['otp'].setValue('', { emitEvent: false });
    this.f['otp'].markAsPristine();
    this.f['otp'].markAsUntouched();
    this.f['otp'].updateValueAndValidity({ emitEvent: false });

    this.showStep1 = false;
    this.showStep2 = true;
  }

  goToStepOne() {
    this.step1Attempted = false;
    this.step2Attempted = false;
    this.f['otp'].setValue('');
    this.otpService.resetOtpInputVal(true);
    this.clearForm();
    this.showStep1 = true;
    this.showStep2 = false;
  }

  ngOnDestroy(): void {
    this.validationAttemptSubscriptions.forEach(sub => sub.unsubscribe());
    this.validationAttemptSubscriptions = [];
  }

  sendOtp() {
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  public pinModalVisible = false;
  public renderPinModalContent = false;

  openModal(name: string = '') {
    if (name === 'PIN') {
      this.goToStepOne();
      this.showSuccess = false;
    }
    this.renderPinModalContent = true;
    this.pinModalVisible = true;
  }

  onTogglePinModal(visible: boolean): void {
    this.pinModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  closeModal() {
    this.goToStepOne();
    this.pinModalVisible = false;
    this.renderPinModalContent = false;
  }
}
